// @flow
import * as React from 'react'
import type { Question } from 'types/question'
import type { Response } from 'types/response'
import type { SessionObject } from 'types/session-object'
import TextAreaField from 'ui/molecules/TextAreaField'
import { hasText } from 'utils/helpers'

import { TriggerUserActions } from '@/components/TriggerUserActions'

import ZentakeFormContext from '../ZentakeFormContext'

type Props = {
  question: Question,
  isOfficeUse: boolean,
  response: Response,
  sessions?: SessionObject,
  mode?: string,
}

function MultiLineQuestion({
  question,
  isOfficeUse,
  response,
  sessions,
  mode,
}: Props): React.Node {
  const formContext = React.useContext(ZentakeFormContext)
  const readonly = formContext.isQuestionReadonly(question)
  const willShowNotAllowedCursor =
    formContext.willShowNotAllowedCursor(question)

  const answer = formContext.getAnswer(question)
  const value =
    answer && answer.answer_text
      ? answer.answer_text
      : question.default_value || ''

  const isDirty = formContext.isQuestionDirty(question)
  const isValid = formContext.isQuestionValid(question)
  const questionError = formContext.getQuestionError(question)

  const [hasError, setHasError] = React.useState(false)
  const [errorMessage, setErrorMessage] = React.useState(
    'This field is required'
  )

  const officeUsePreview = mode === 'preview' && question.is_office_use_only

  React.useEffect(() => {
    setHasError(
      (isDirty && question?.is_required && !isValid) || hasText(questionError)
    )
    setErrorMessage(
      questionError && typeof questionError === 'string'
        ? questionError
        : 'This field is required'
    )
  }, [isDirty, question.is_required, isValid, questionError])

  function handleChange(value: string) {
    formContext.onAnswerChanged(question, value)
  }

  return (
    <TriggerUserActions>
      <TextAreaField
        data-form-id={response?.form?.id}
        data-packet-id={
          sessions?.fillResponseCustomer?.packetResponse?.packet?.id
        }
        data-response-id={response?.id}
        disabled={readonly}
        errorLabel={errorMessage}
        hasError={hasError}
        id={`q-${question.id}`}
        isOfficeUse={isOfficeUse || officeUsePreview}
        isRequired={question.is_required}
        label={question.text}
        onChange={handleChange}
        value={value}
        willShowNotAllowedCursor={willShowNotAllowedCursor}
      />
    </TriggerUserActions>
  )
}

export default MultiLineQuestion
